<template>
  <div>
    <v-card
      data-component-id="RecruiterStatusRecommended"
    >
      <v-card-title
        class="text-h5"
      >
        Saisissez l’ETP pourvus
      </v-card-title>
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
      />
      <v-card-text>
        <v-form
          v-model="isValidForm"
        >
          <template
            v-for="(field, fieldIndex) in fields"
          >
            <div
              :key="fieldIndex"
            >
              <GenericField
                v-model="data[field.name]"
                :field="{
                  ...field,
                  disabled: isLoading
                }"
              />
            </div>
          </template>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="handleDisagree"
        >
          Annuler
        </v-btn>
        <v-btn
          :disabled="!isValidForm || isLoading"
          color="primary"
          text
          @click="handleAccept"
        >
          Accepter
        </v-btn>
      </v-card-actions>
    </v-card>

    <RecruiterMultiApplicationToValidate
      :model-value="dialog"
      :is-loading="isLoading"
      :applications="otherActiveApplications"
      @ignore="handleIgnore"
      @update-all="handleUpdateAll"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import moment from 'moment';

import { requests } from '@/mixins/requests';

import GenericField from '@/components/dynamic/form/templates/GenericField.vue';
import RecruiterMultiApplicationToValidate from '@/components/application/components/RecruiterMultiApplicationToValidate.vue';

export default {
  name: 'RecruiterStatusRecommended',
  components: { GenericField, RecruiterMultiApplicationToValidate },
  props: {
    fields: {
      type: Array,
      default: () => []
    },
    item: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      data: {},
      isValidForm: false,
      isLoading: false,

      dialog: false,
      otherActiveApplications: []
    };
  },
  computed: { ...mapState(['user']) },
  watch: {
    item: {
      handler () {
        this.initializeData();
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    getNestedValue (obj, path) {
      return path
        .split('.')
        .reduce((acc, part) => (acc && acc[part]), obj);
    },
    initializeData () {
      const item = JSON.parse(JSON.stringify(this.item));

      this.fields.forEach(field => {
        this.$set(
          this.data,
          field.name,
          field.computed ? this.getNestedValue(item, field.computed) : item[field.name]
        );
      });
    },
    generateUniqueId () {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 15);
      const uniqueId = `${timestamp}_${random}`;
      return uniqueId;
    },
    navigateAfterSave () {
      this.isLoading = false;
      this.$emit('close-dialog');

      const routeMapping = {
        candidature: { name: 'offer-candidatures', params: { offerUuid: this.$route.params.offerUuid } },
        'candidature-spontanee': { name: 'candidature-spontanee-list' }
      };

      const targetRoute = routeMapping[this.$router.currentRoute.name];
      if (targetRoute) {
        this.$router.push(targetRoute);
      }
    },
    async handleAccept () {
      this.$emit('handle-loading', true);
      this.isLoading = true;

      const {
        coveredETPS,
        message
      } = this.data;

      let transformedNotes = null;

      if (['candidatures', 'offer-candidatures', 'candidature-spontanee-list'].includes(this.$router.currentRoute.name)) {
        const response = await this.fetchApplication();

        this.data.notes = response.notes || [];
      }

      if (this.item.notes) {
        this.data.notes = this.item.notes;
      }

      if (!this.data.notes) {
        this.data.notes = [];
      }

      transformedNotes = this.data.notes.map(note => ({
        ...note,
        collaborator: note.collaborator?.uuid || note.collaborator
      }));

      if (message) {
        transformedNotes.push({
          id: `note_${this.generateUniqueId()}`,
          collaborator: this.user.uuid,
          text: message,
          createTime: `${moment().toISOString()}`
        });
      }

      const response = await this.saveApplication({
        workspace: 'collaborator',
        status: 'draft',
        uuid: this.item.uuid,

        coveredETPS: Number(coveredETPS),
        notes: transformedNotes,

        collaborator: this.item.collaborator?.uuid,
        recruiter: this.user.uuid,
        recruiterStatus: 'HIRED',
        prospectStatus: 'HIRED',
        stared: true
      }, true);

      if (response.success && response.data?.otherApplications?.length) {
        this.otherActiveApplications = response.data.otherApplications;
        this.dialog = true;

        this.isLoading = false;
        return;
      }

      this.navigateAfterSave();

      this.$emit('handle-loading', false);
    },
    async saveApplication (input, isEditMode) {
      const response = await requests.saveApplication({
        input,
        isEditMode: isEditMode
      });

      if (!response.success) {
        this.$store.commit('setSnackbar', { text: 'Erreur lors de l\'enregistrement.', color: 'error' });
        this.handleDisagree();
      } else {
        this.$store.commit('setSnackbar', { text: 'Enregistrer avec succès.', color: 'success' });
      }

      return response;
    },
    async fetchApplication () {
      this.$emit('handle-loading', true);

      const response = await requests.getApplication(this.item.uuid);

      if (response?.success) {
        return response.data;
      }

      this.$emit('handle-loading', false);

      return null;
    },
    handleDisagree () {
      this.$emit('disagree');
      this.$emit('handle-loading', false);
    },
    handleIgnore () {
      this.dialog = false;
      this.$emit('handle-loading', false);
      this.navigateAfterSave();
    },
    async handleUpdateAll () {
      this.isLoading = true;
      this.$emit('handle-loading', true);

      try {
        const response = await requests.updateRelatedApplications({
          uuid: this.item.uuid,
          otherApplications: this.otherActiveApplications.map(application => application.uuid)
        });

        if (response.success) {
          this.$store.commit('setSnackbar', { text: 'Candidatures mises à jour avec succès.', color: 'success' });
        }
      } catch (error) {
        this.$store.commit('setSnackbar', { text: 'Erreur lors de la mise à jour des candidatures.', color: 'error' });
      } finally {
        this.dialog = false;

        this.isLoading = false;
        this.$emit('handle-loading', false);
      }
    },
    getInputClasses (field) {
      const classes = [field.mandatory ? 'required' : ''];

      if (this.isViewMode) classes.push('input-color-black');

      return classes.join(' ');
    }
  }
};
</script>
